import {
  BoardTimePicker,
  StartTimePicker
} from '@library/components/simulations';
import { DateTime } from 'luxon';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import { Tooltip } from '@library/components/tooltip/Tooltip';
import { useState } from 'react';

export interface BulkTimePickersHeaderProps {
  isTopLevel?: boolean;
  simProjections: Array<GamePlayerProjectionTrading>;
  onBulkTimeChange: ({
    projectionBoardTime,
    projectionStartTime,
    simsProjections,
    updateProjection
  }: {
    simsProjections: Array<GamePlayerProjectionTrading>;
    projectionBoardTime?: string;
    projectionStartTime?: string;
    updateProjection: ({
      change,
      initialProjection
    }: {
      change: GamePlayerProjectionTrading;
      initialProjection: GamePlayerProjectionTrading;
    }) => void;
  }) => void;
  updateProjection: ({
    change,
    initialProjection
  }: {
    change: GamePlayerProjectionTrading;
    initialProjection: GamePlayerProjectionTrading;
  }) => void;
}

export const BulkTimePickers = ({
  isTopLevel = false,
  onBulkTimeChange,
  simProjections,
  updateProjection
}: BulkTimePickersHeaderProps) => {
  const [bulkBoardTime, setBulkBoardTime] = useState<DateTime>();
  const [bulkStartTime, setBulkStartTime] = useState<DateTime>();

  const initialBoardTime = simProjections[simProjections.length - 1].boardTime;
  const initialStartTime = simProjections[simProjections.length - 1].startTime;

  const handleBulkTimeChange = (
    boardTime: DateTime,
    startTime: DateTime,
    key: string
  ) => {
    const boardTimeString = boardTime.toISO();
    const startTimeString = startTime.toISO();

    if (
      boardTimeString !== initialBoardTime &&
      startTimeString !== initialStartTime &&
      boardTimeString &&
      startTimeString
    ) {
      setBulkBoardTime(DateTime.fromISO(boardTimeString));
      setBulkStartTime(DateTime.fromISO(startTimeString));
      onBulkTimeChange({
        simsProjections: simProjections,
        projectionBoardTime: boardTimeString,
        projectionStartTime: startTimeString,
        updateProjection
      });
    } else if (key === 'startTime' && startTimeString) {
      setBulkStartTime(DateTime.fromISO(startTimeString));
      onBulkTimeChange({
        simsProjections: simProjections,
        projectionStartTime: startTimeString,
        updateProjection
      });
    } else if (key === 'boardTime' && boardTimeString) {
      setBulkBoardTime(DateTime.fromISO(boardTimeString));
      onBulkTimeChange({
        simsProjections: simProjections,
        projectionBoardTime: boardTimeString,
        updateProjection
      });
    }
  };

  return (
    <div className={`flex items-center gap-4 ${isTopLevel && 'justify-end'}`}>
      <Tooltip text="Change Board Time for all below projections">
        <div className="flex items-center gap-4">
          <span className="font-heebo text-soClean-140 text-sm font-medium tracking-wide">
            Bulk Board Time
          </span>
          <BoardTimePicker
            boardTime={bulkBoardTime?.toISO() || initialBoardTime}
            handleChange={(boardTime) => {
              handleBulkTimeChange(
                boardTime,
                bulkStartTime || DateTime.fromISO(initialStartTime),
                'boardTime'
              );
            }}
            initialBoardTime={initialBoardTime}
            startTime={
              bulkStartTime?.toISO() ||
              simProjections[simProjections.length - 1].startTime
            }
          />
        </div>
      </Tooltip>
      <Tooltip text="Change Start Time for all below projections">
        <div className="flex items-center gap-4">
          <span className="font-heebo text-soClean-140 text-sm font-medium tracking-wide">
            Bulk Start Time
          </span>

          <StartTimePicker
            handleChange={(startTime) => {
              handleBulkTimeChange(
                bulkBoardTime || DateTime.fromISO(initialBoardTime),
                startTime,
                'startTime'
              );
            }}
            initialStartTime={initialStartTime}
            startTime={
              bulkStartTime?.toISO() ||
              simProjections[simProjections.length - 1].startTime
            }
          />
        </div>
      </Tooltip>
    </div>
  );
};
